import React, { useState, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Zap, Leaf, BarChart3 } from "lucide-react";
import { AuthContext } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    await login(email, password);
    setIsLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error("Veuillez saisir votre adresse email");
      return;
    }

    setResetLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      console.log("Sending password reset to:", API);
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      console.log("Password reset response:", response);
      toast.success("Email de réinitialisation envoyé ! Vérifiez votre boîte mail.");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      console.error("Password reset error:", error);
      if (error.response?.status === 404) {
        toast.error("Aucun compte trouvé avec cette adresse email");
      } else {
        toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-100 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/20 to-blue-50/20 opacity-30"></div>
      
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left Side - Branding & Features */}
        <div className="space-y-8">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_ccca3806-d8f7-44f9-8ff2-bbd147115d9d/artifacts/5h8z3m6s_Logo%20Easypower.png"
              alt="Easy Power Logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Easy Power</h1>
              <p className="text-emerald-600 font-medium">CRM de Courtage en Énergie</p>
            </div>
          </div>

          {/* Hero Content */}
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-800 leading-tight">
              Optimisez vos contrats énergétiques
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Gérez vos clients, simulez des offres et automatisez le suivi des échéances 
              contractuelles en toute simplicité.
            </p>
          </div>

          {/* Features */}
          <div className="grid gap-4">
            <div className="flex items-center space-x-4 p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-white/50">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Zap className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Alertes Automatisées</h3>
                <p className="text-slate-600 text-sm">Suivez les échéances contractuelles en temps réel</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-white/50">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Simulateur d'Offres</h3>
                <p className="text-slate-600 text-sm">Comparez automatiquement les fournisseurs</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-white/50">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Leaf className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Énergie Verte</h3>
                <p className="text-slate-600 text-sm">Intégrez les critères environnementaux</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto">
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-1 pb-8">
              <CardTitle className="text-2xl font-bold text-center">Connexion</CardTitle>
              <CardDescription className="text-center text-slate-600">
                Accédez à votre espace de gestion énergétique
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12"
                    data-testid="email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12"
                    data-testid="password-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 energy-primary text-white font-medium"
                  disabled={isLoading}
                  data-testid="login-button"
                >
                  {isLoading ? "Connexion..." : "Se connecter"}
                </Button>
                
                {/* Forgot Password Link */}
                <div className="text-center">
                  <button
                    type="button"
                    className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-slate-500">
            <p>© 2024 Easy Power. Tous droits réservés.</p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Réinitialiser le mot de passe</h3>
            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={resetLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                  disabled={resetLoading}
                >
                  {resetLoading ? "Envoi..." : "Envoyer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;